import { FormikProps } from 'formik';
import React, { useMemo } from 'react';
import { ProjectGq } from '../../../../../../../graphql/projects/models/ProjectGq';
import VoxelAutocomplete, { GenericOption } from '../../../../../../voxel/Forms/VoxelAutocomplete';
import VoxelCheckbox from '../../../../../../voxel/Forms/VoxelCheckbox';
import VoxelText from '../../../../../../voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../../../../voxel/Tooltip/VoxelTooltipShort';
import VoxelStepCounter from '../../../../../../voxel/VoxelStepCounter';
import { CreateSearchFormState } from './logic/SearchModels';

export interface AddTagsSectionProps {
  project: ProjectGq;
  formik: FormikProps<CreateSearchFormState>;
  disabled?: boolean;
}

const AddTagsSection: React.FC<AddTagsSectionProps> = ({
  project,
  formik,
  disabled,
}) => {

  const tagGroupOptions: GenericOption[] = useMemo(() => {
    return [...project?.tagGroups?.map(v => ({ value: v.id, title: v.name }))]
  }, [project.tagGroups]);

  const tagOptions: GenericOption[] = useMemo(() => {
    if (isNaN(formik.values.tagKeywords?.tagGroupId)) return [];
    const tgId = formik.values.tagKeywords.tagGroupId;
    const tagOptions: GenericOption[] = project?.tagGroups?.find(tg => tg.id === tgId)?.tags?.map(v => ({ value: v.id, title: v.name }));
    return [...tagOptions]
  }, [project.tagGroups, formik.values.tagKeywords]);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-row items-center gap-8'>
        <div className='mx-2 pt-5'>
          <VoxelStepCounter title='1' active={true} />
        </div>
        <VoxelAutocomplete
          label={'Select or add group'}
          labelPosition="top"
          placeholder={'Group'}
          options={tagGroupOptions}
          selectedOptionValues={
            formik.values.tagKeywords?.tagGroupId ? [formik.values.tagKeywords.tagGroupId] : undefined
          }
          setSelectedOptions={(option) => {
            formik.setFieldValue('tagKeywords', {
              tagGroupId: option[0]?.value,
              tagGroupName: option[0]?.title,
              tagId: null,
              tagName: null,
              useSeedKeywords: false,
            })
          }}
          emptyAllowed={true}
          freeAdd={true}
          inputWidthClass="w-full"
          searchPlaceholder="Search or add"
        />
      </div>
      <div className='flex flex-row items-center gap-8'>
        <div className='mx-2 pt-5'>
          <VoxelStepCounter title='2' active={!(disabled || !formik.values.tagKeywords?.tagGroupId)} />
        </div>
        <div className='grow flex flex-row items-center flex-nowrap gap-8 justify-items-center'>
          <VoxelTooltipShort message='All resulting keywords will be tagged with their respective seed keyword'>
            <div className='pt-5'>
              <VoxelCheckbox
                checked={formik.values.tagKeywords?.useSeedKeywords}
                onChange={(value) => {
                  const prev = formik.values.tagKeywords;
                  formik.setFieldValue('tagKeywords', {
                    tagGroupId: prev.tagGroupId,
                    tagGroupName: prev.tagGroupName,
                    tagId: null,
                    tagName: null,
                    useSeedKeywords: value,
                  })
                }}
                label='Tag with seed keyword'
                disabled={disabled || !formik.values.tagKeywords?.tagGroupId}
              />
            </div>
          </VoxelTooltipShort>
          <VoxelText className='pt-5' color={disabled || !formik.values.tagKeywords?.tagGroupId ? 'hint' : 'medium'}>or</VoxelText>
          <VoxelAutocomplete
            disabled={disabled || !formik.values.tagKeywords?.tagGroupId}
            label={'Select or add tag'}
            labelPosition="top"
            placeholder={'Tag'}
            options={tagOptions}
            selectedOptionValues={
              formik.values.tagKeywords?.tagId ? [formik.values.tagKeywords?.tagId] : undefined
            }
            setSelectedOptions={(option) => {
              const prev = formik.values.tagKeywords;
              formik.setFieldValue('tagKeywords', {
                tagGroupId: prev.tagGroupId,
                tagGroupName: prev.tagGroupName,
                tagId: option[0]?.value,
                tagName: option[0]?.title,
                useSeedKeywords: false,
              })
            }}
            emptyAllowed={true}
            freeAdd={true}
            inputWidthClass="w-full"
            searchPlaceholder="Search or add"
          />
        </div>
      </div>
    </div>
  );
};

export default AddTagsSection;
